import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';
import { countries, Country, getCountryName } from '../data/countries';

interface MultiSelectCountriesProps {
  selectedCountries: string[];
  onChange: (countries: string[]) => void;
  placeholder?: string;
  className?: string;
}

export const MultiSelectCountries: React.FC<MultiSelectCountriesProps> = ({
  selectedCountries,
  onChange,
  placeholder = "Select countries...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter countries based on search term
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleCountryToggle = (countryCode: string) => {
    if (selectedCountries.includes(countryCode)) {
      onChange(selectedCountries.filter(code => code !== countryCode));
    } else {
      onChange([...selectedCountries, countryCode]);
    }
  };

  const handleRemoveCountry = (countryCode: string, event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(selectedCountries.filter(code => code !== countryCode));
  };

  const handleClearAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange([]);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Main Input */}
      <div
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent cursor-pointer bg-white min-h-[42px] flex items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex-1 flex flex-wrap gap-1">
          {selectedCountries.length === 0 ? (
            <span className="text-gray-500">{placeholder}</span>
          ) : (
            selectedCountries.map(countryCode => (
              <span
                key={countryCode}
                className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
              >
                <span className="font-mono mr-1">{countryCode}</span>
                <span className="mr-1">{getCountryName(countryCode)}</span>
                <button
                  type="button"
                  onClick={(e) => handleRemoveCountry(countryCode, e)}
                  className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X size={12} />
                </button>
              </span>
            ))
          )}
        </div>
        
        <div className="flex items-center gap-2 ml-2">
          {selectedCountries.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-gray-400 hover:text-gray-600 p-1"
              title="Clear all"
            >
              <X size={16} />
            </button>
          )}
          <ChevronDown 
            size={20} 
            className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search countries..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Countries List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredCountries.length === 0 ? (
              <div className="p-3 text-gray-500 text-sm text-center">
                No countries found
              </div>
            ) : (
              filteredCountries.map(country => (
                <div
                  key={country.code}
                  className={`px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between ${
                    selectedCountries.includes(country.code) ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleCountryToggle(country.code)}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedCountries.includes(country.code)}
                      onChange={() => {}} // Handled by parent click
                      className="mr-3 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="font-mono text-sm text-gray-600 mr-2 min-w-[3rem]">
                      {country.code}
                    </span>
                    <span className="text-sm">{country.name}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {selectedCountries.length > 0 && (
            <div className="p-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-600">
              {selectedCountries.length} countr{selectedCountries.length === 1 ? 'y' : 'ies'} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
};
